import React, { useMemo, useState } from 'react';
import { accountingApi } from '../../services/accountingApi';

const EMPTY_COMPANY = {
    business_name: '', commercial_name: '', ruc: '', alias: '', fiscal_address: '',
    department: '', province: '', district: '', ubigeo: '', phone: '', email: '',
    legal_representative: '', is_active: 1,
};

const EMPTY_SUNAT = {
    sol_user: '', sol_password: '', certificate_password: '', cdt_value: '',
    environment: 'PRODUCTION', certificate_name: '', certificate_base64: '',
    certificate_expires_at: '', gre_client_id: '', gre_client_secret: '',
    gre_validation_status: 'PENDING', gre_last_validation_at: null, gre_last_validation_message: '',
};

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

export default function AccountingRucCompanies({ meta, reload }) {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState('company');
    const [company, setCompany] = useState(EMPTY_COMPANY);
    const [sunat, setSunat] = useState(EMPTY_SUNAT);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testingGre, setTestingGre] = useState(false);
    const canEdit = meta.permissions?.canEdit === true;
    const rows = useMemo(() => meta.rucs || [], [meta.rucs]);

    const reset = () => {
        setCompany(EMPTY_COMPANY);
        setSunat(EMPTY_SUNAT);
        setTab('company');
    };

    const close = () => {
        setOpen(false);
        reset();
    };

    const create = () => {
        reset();
        setOpen(true);
    };

    const edit = async (row) => {
        try {
            const response = await accountingApi.getRucCompany(row.id);
            setCompany({ ...EMPTY_COMPANY, ...(response.company || row) });
            setSunat({ ...EMPTY_SUNAT, ...(response.sunat || {}) });
            setOpen(true);
        } catch (error) {
            alert(error.response?.data?.message || 'No se pudo consultar la empresa.');
        }
    };

    const changeCompany = (name, value) => setCompany((previous) => ({ ...previous, [name]: value }));
    const changeSunat = (name, value) => setSunat((previous) => ({ ...previous, [name]: value }));

    const loadCertificate = (file) => {
        if (!file) return;
        const extension = file.name.toLowerCase().split('.').pop();
        if (!['p12', 'pfx'].includes(extension)) {
            alert('El certificado debe ser un archivo .p12 o .pfx.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('El certificado no puede superar 5 MB.');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = String(reader.result || '').split(',')[1] || '';
            setSunat((previous) => ({
                ...previous,
                certificate_name: file.name,
                certificate_base64: base64,
            }));
        };
        reader.onerror = () => alert('No se pudo leer el certificado.');
        reader.readAsDataURL(file);
    };

    const validate = () => {
        if (!company.business_name.trim()) return 'Ingresa la razón social.';
        if (!/^\d{11}$/.test(onlyDigits(company.ruc))) return 'El RUC debe tener exactamente 11 dígitos.';
        if (company.email && !/^\S+@\S+\.\S+$/.test(company.email)) return 'Ingresa un correo válido.';
        return '';
    };

    const save = async () => {
        const message = validate();
        if (message) return alert(message);
        setSaving(true);
        try {
            const response = await accountingApi.saveRucCompany({ company, sunat });
            await reload();
            alert(response.message || 'Empresa guardada correctamente.');
            close();
        } catch (error) {
            alert(error.response?.data?.message || 'No se pudo guardar la empresa.');
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        if (!company.id) return alert('Primero guarda la empresa.');
        setTesting(true);
        try {
            const response = await accountingApi.testSunatConfiguration(company.id);
            alert(response.message || 'Validación completada.');
            const detail = await accountingApi.getRucCompany(company.id);
            setSunat({ ...EMPTY_SUNAT, ...(detail.sunat || {}) });
            await reload();
        } catch (error) {
            alert(error.response?.data?.message || 'La configuración SUNAT no superó la validación.');
        } finally {
            setTesting(false);
        }
    };

    const testGre = async () => {
        if (!company.id) return alert('Primero guarda la empresa.');
        setTestingGre(true);
        try {
            const response = await accountingApi.testGreConfiguration(company.id);
            alert(response.message || 'Autenticación GRE validada correctamente.');
            const detail = await accountingApi.getRucCompany(company.id);
            setSunat({ ...EMPTY_SUNAT, ...(detail.sunat || {}) });
            await reload();
        } catch (error) {
            const detail = await accountingApi.getRucCompany(company.id).catch(() => null);
            if (detail?.sunat) setSunat({ ...EMPTY_SUNAT, ...detail.sunat });
            alert(error.response?.data?.message || 'SUNAT rechazó la autenticación GRE.');
        } finally {
            setTestingGre(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('¿Seguro que deseas desactivar esta empresa?')) return;
        try {
            await accountingApi.deleteCatalog('rucs', id);
            await reload();
        } catch (error) {
            alert(error.response?.data?.message || 'No se pudo desactivar la empresa.');
        }
    };

    const field = (label, name, options = {}) => (
        <label className={options.wide ? 'rucField wide' : 'rucField'}>
            <span>{label}</span>
            <input
                type={options.type || 'text'}
                value={options.source === 'sunat' ? sunat[name] || '' : company[name] || ''}
                maxLength={options.maxLength}
                placeholder={options.placeholder || ''}
                autoComplete={options.autoComplete || 'off'}
                onChange={(event) => {
                    const value = options.digits ? onlyDigits(event.target.value) : event.target.value;
                    if (options.source === 'sunat') changeSunat(name, value);
                    else changeCompany(name, value);
                }}
            />
        </label>
    );

    return (
        <section className="accCard rucCompaniesCard">
            <div className="rucCompaniesHeader">
                <div>
                    <span className="rucEyebrow">Empresas y SUNAT</span>
                    <h3>RUC empresariales</h3>
                    <p>Administra la identidad fiscal y las credenciales SUNAT de cada empresa.</p>
                </div>
                {canEdit && <button type="button" className="accPrimary rucAddButton" onClick={create}>+ Agregar empresa</button>}
            </div>

            <div className="rucCompanyList">
                {rows.map((row) => (
                    <article className="rucCompanyRow" key={row.id}>
                        <div className="rucCompanyIdentity">
                            <div className="rucCompanyInitial">{String(row.alias || row.business_name || 'E').slice(0, 1)}</div>
                            <div>
                                <strong>{row.business_name}</strong>
                                <span>{row.ruc} {row.alias ? `• ${row.alias}` : ''}</span>
                            </div>
                        </div>
                        <div className={`sunatStatus ${row.sunat_validation_status === 'VALID' && row.gre_validation_status === 'VALID' ? 'valid' : 'pending'}`}>
                            <i />
                            {row.sunat_validation_status === 'VALID' ? 'SUNAT ✓' : 'SUNAT pendiente'} · {row.gre_validation_status === 'VALID' ? 'GRE ✓' : 'GRE pendiente'}
                        </div>
                        <div className="rucCompanyActions">
                            <button type="button" onClick={() => edit(row)}>Ver / Editar</button>
                            {canEdit && <button type="button" className="danger" onClick={() => remove(row.id)}>Eliminar</button>}
                        </div>
                    </article>
                ))}
                {!rows.length && <div className="rucEmpty">Aún no existen empresas registradas.</div>}
            </div>

            {open && (
                <div className="rucModalBackdrop" role="presentation" onMouseDown={close}>
                    <section className="rucModal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
                        <header className="rucModalHeader">
                            <div>
                                <span>{company.id ? 'Editar empresa' : 'Nueva empresa'}</span>
                                <h2>{company.business_name || 'Configuración empresarial'}</h2>
                            </div>
                            <button type="button" className="rucModalClose" onClick={close}>×</button>
                        </header>

                        <nav className="rucModalTabs">
                            <button type="button" className={tab === 'company' ? 'active' : ''} onClick={() => setTab('company')}>Información general</button>
                            <button type="button" className={tab === 'sunat' ? 'active' : ''} onClick={() => setTab('sunat')}>SUNAT y certificado</button>
                        </nav>

                        <div className="rucModalBody">
                            {tab === 'company' && (
                                <div className="rucFormGrid">
                                    {field('Razón social *', 'business_name', { wide: true })}
                                    {field('Nombre comercial', 'commercial_name')}
                                    {field('RUC *', 'ruc', { digits: true, maxLength: 11 })}
                                    {field('Alias', 'alias')}
                                    {field('Dirección fiscal', 'fiscal_address', { wide: true })}
                                    {field('Departamento', 'department')}
                                    {field('Provincia', 'province')}
                                    {field('Distrito', 'district')}
                                    {field('Ubigeo', 'ubigeo', { digits: true, maxLength: 6 })}
                                    {field('Teléfono', 'phone')}
                                    {field('Correo', 'email', { type: 'email' })}
                                    {field('Representante legal', 'legal_representative', { wide: true })}
                                </div>
                            )}

                            {tab === 'sunat' && (
                                <div className="sunatPanel">
                                    <div className="sunatSecurityNotice">
                                        <strong>Credenciales protegidas</strong>
                                        <span>Las claves y el certificado se cifran en el backend. Nunca se retornan al navegador.</span>
                                    </div>
                                    <div className="rucFormGrid">
                                        {field('Usuario SOL', 'sol_user', { source: 'sunat', autoComplete: 'new-password' })}
                                        {field('Clave SOL', 'sol_password', { source: 'sunat', type: 'password', autoComplete: 'new-password', placeholder: sunat.has_sol_password ? '•••••••• (guardada)' : '' })}
                                        {field('Contraseña del certificado', 'certificate_password', { source: 'sunat', type: 'password', autoComplete: 'new-password', placeholder: sunat.has_certificate_password ? '•••••••• (guardada)' : '' })}
                                        {field('Fecha de vencimiento', 'certificate_expires_at', { source: 'sunat', type: 'date' })}
                                        <label className="rucField">
                                            <span>Ambiente</span>
                                            <select value={sunat.environment || 'PRODUCTION'} onChange={(event) => changeSunat('environment', event.target.value)}>
                                                <option value="BETA">Beta / homologación</option>
                                                <option value="PRODUCTION">Producción</option>
                                            </select>
                                        </label>
                                        {field('CDT / identificador', 'cdt_value', { source: 'sunat', type: 'password', autoComplete: 'new-password', placeholder: sunat.has_cdt_value ? '•••••••• (guardado)' : '' })}
                                        <label className="rucField wide certificateDrop">
                                            <span>Certificado digital (.p12 o .pfx)</span>
                                            <input type="file" accept=".p12,.pfx,application/x-pkcs12" onChange={(event) => loadCertificate(event.target.files?.[0])} />
                                            <small>{sunat.certificate_name || 'No se ha seleccionado un certificado.'}</small>
                                        </label>
                                    </div>

                                    <div className="greCredentialsCard">
                                        <div className="greCredentialsHeader">
                                            <div>
                                                <span>API GRE SUNAT · por RUC</span>
                                                <strong>Credenciales para Guía de Remisión Electrónica</strong>
                                                <small>El usuario y la clave SOL de arriba se reutilizan. Aquí debes registrar el client_id y client_secret creados en “Credenciales de API SUNAT”.</small>
                                            </div>
                                            <div className={`greStatusBadge ${(sunat.gre_validation_status || 'PENDING').toLowerCase()}`}>
                                                {sunat.gre_validation_status === 'VALID' ? 'OAuth GRE válido' : sunat.gre_validation_status === 'INVALID' ? 'OAuth/API GRE con incidencia' : 'GRE pendiente'}
                                            </div>
                                        </div>
                                        <div className="rucFormGrid">
                                            {field('GRE client_id', 'gre_client_id', { source: 'sunat', wide: true, placeholder: 'Client ID generado en SUNAT Operaciones en Línea' })}
                                            {field('GRE client_secret', 'gre_client_secret', { source: 'sunat', wide: true, type: 'password', autoComplete: 'new-password', placeholder: sunat.has_gre_client_secret ? '•••••••• (guardado y cifrado)' : 'Client Secret generado por SUNAT' })}
                                        </div>
                                        <div className="greHelpText">
                                            <strong>Importante:</strong> SUNAT autentica la GRE con <code>RUC + Usuario SOL</code>, <code>Clave SOL</code>, <code>client_id</code> y <code>client_secret</code>. El client_secret nunca se devuelve al navegador.
                                        </div>
                                        {company.id && (
                                            <div className="sunatValidationBar greValidationBar">
                                                <div>
                                                    <strong>Estado GRE: {sunat.gre_validation_status === 'VALID' ? 'OAuth validado con SUNAT' : sunat.gre_validation_status === 'INVALID' ? 'Autenticación/API con incidencia' : 'Pendiente de validar'}</strong>
                                                    <span>{sunat.gre_last_validation_message || 'Guarda los datos y valida la autenticación contra SUNAT sin emitir ninguna guía.'}</span>
                                                </div>
                                                <button type="button" disabled={testingGre || !canEdit} onClick={testGre}>{testingGre ? 'Conectando con SUNAT...' : 'Validar GRE'}</button>
                                            </div>
                                        )}
                                    </div>

                                    {company.id && (
                                        <div className="sunatValidationBar">
                                            <div>
                                                <strong>Estado: {sunat.validation_status === 'VALID' ? 'Validado' : 'Pendiente'}</strong>
                                                <span>{sunat.last_validation_message || 'Guarda las credenciales y ejecuta la validación técnica.'}</span>
                                            </div>
                                            <button type="button" disabled={testing || !canEdit} onClick={test}>{testing ? 'Validando...' : 'Validar configuración'}</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <footer className="rucModalFooter">
                            <button type="button" className="secondary" onClick={close}>Cancelar</button>
                            <button type="button" className="primary" disabled={saving || !canEdit} onClick={save}>{saving ? 'Guardando...' : 'Guardar empresa'}</button>
                        </footer>
                    </section>
                </div>
            )}
        </section>
    );
}
